import { Cohort, ErrorCode } from '@afterpaytouch/portal-api/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { parseCookies } from 'nookies'
import {
  CheckoutAppState,
  checkoutInitialDataSelector,
  checkoutSelector,
  checkoutStatusSelector,
  consumerSelector,
  convergenceCohortSelector,
  isRecoverableFlow,
  mobileLoginSelector,
  oAuth2faHeaders,
  portalAction,
  PortalThunk,
  PortalThunkConfig,
  profilingIdSelector,
  recoverableTerminalErrorsStateSelector,
  tokenSelector,
} from '../../'
import {
  AFTERPAY_CHECKOUT_LOGIN_TOKEN,
  APPLE_ORDER_SOURCES,
  AuthLoginRequest,
  AuthLoginResponse,
  CASH_CHECKOUT_TOKEN,
  CheckoutStatus,
  CheckoutTokenResponse,
  CHECKOUT_TOKEN_REQUEST,
  PaymentMethodType,
  PersistentLoginHeaders,
  SendResetLinkRequest,
  VerificationMethod,
  EFA_CHECK_STATUS,
  OAuth2faHeaders,
} from '../../../model'
import { createFingerPrint } from '../../../utils'
import { isAfterpayInAppBrowser, isCashAppInAppBrowser } from '../../../utils/device'
import { FLOW_COOKIE_NAME, FlowCookieValue } from '../../../utils/server/flowCookie'
import { hasOptedOutRememberMe } from '../../../flow/remember-me/optOut'
import { afterpayCheckoutLoginTokenSelector, authenticationSelector, cashCheckoutTokenSelector, isTrustedCheckoutSelector } from './hooks'
import jwtDecode from 'jwt-decode'
import { isCallable } from '@afterpay/utils'
import { RecoverableTerminalErrorStatus } from '@afterpay/types'

export const createFingerprint = createAsyncThunk('user/fingerprint', async () => await createFingerPrint())

// Mirrors useIsApplePaylater (state/applePaylater/hooks.ts) for use outside a hook: the Apple Pay
// Later flow shares LoginPassword but never mounts useMintPersistentSessionOnLogin or the issuance
// toast, so remember-me must stay out of that surface entirely.
const isApplePaylaterFlow = (state: CheckoutAppState): boolean => {
  const flowCookie = parseCookies()[FLOW_COOKIE_NAME] as FlowCookieValue | undefined
  const source = checkoutSelector(state).checkout?.merchant?.source
  return flowCookie === FlowCookieValue.ApplePaylater && source != null && APPLE_ORDER_SOURCES.includes(source)
}

// State-level equivalent of `usePostLoginRoute() === Route.LOGGED_IN` (config/router.ts): a login
// only lands on LoggedIn when no recoverable-error flow is active and the checkout has not started.
// A mid-flow re-login (e.g. after a 401 expired the session) routes straight back into the flow,
// where both markInteractiveLogin call sites deliberately skip the remember-me signal — the step-up
// param must stay off that surface too, or a NY re-login would force an OTP and mint without the
// normal issuance path.
const isFreshLoginSurface = (state: CheckoutAppState): boolean =>
  !isRecoverableFlow(recoverableTerminalErrorsStateSelector(state)?.status ?? RecoverableTerminalErrorStatus.NotStarted) &&
  checkoutStatusSelector(state) < CheckoutStatus.Starting

/**
 * CASHDAP-3557: whether this password login asks portal-api to arm a remember-me mint via the
 * dedicated `remember-me-step-up` param (plain `remember-me` stays false — that name is the real
 * checkbox on the portal and legacy Angular-checkout login forms, with legacy login-time-mint
 * semantics this surface must not trigger). The server honours the step-up param ONLY for
 * restricted-state (NY) residents inside the server-side CHECKOUT_LONG_LIVE_SESSION_ENABLED ramp,
 * whose fixed-window session must be minted through a true two-factor step-up: the param escalates
 * the login to an OTP (requires2fa in the response — the existing 2FA flow) and the session is
 * minted after that second factor. For every other consumer the server drops the param and the
 * mint stays on the post-login POST /persistent-login/new (useMintPersistentSessionOnLogin), where
 * the uuid-keyed LD flag can be evaluated — impossible here, pre-login, where the LD context is
 * still anonymous. A portal-api without the param's handling ignores it entirely, so this is inert
 * until the server side deploys. Gates mirror that hook's device/session eligibility, plus the
 * Apple Pay Later exclusion (that flow shares LoginPassword but not the remember-me machinery),
 * plus the fresh-login-surface check that markInteractiveLogin already applies (a mid-flow
 * re-login after session expiry must not request the mint).
 * Password login only: OTP login is single-factor (its mint would not satisfy the
 * two-factor-at-issuance requirement) and biometric logins cannot carry form params.
 */
const rememberMeStepUpLoginParam = (state: CheckoutAppState): boolean =>
  convergenceCohortSelector(state) === Cohort.APOnly &&
  !authenticationSelector(state).persistent &&
  !isTrustedCheckoutSelector(state) &&
  checkoutInitialDataSelector(state)?.isIframe !== true &&
  isFreshLoginSurface(state) &&
  !isApplePaylaterFlow(state) &&
  !isAfterpayInAppBrowser() &&
  !isCashAppInAppBrowser() &&
  !hasOptedOutRememberMe()

export const authLogin: PortalThunk<AuthLoginRequest, AuthLoginResponse> = portalAction('login', async (request, thunk) => {
  const state = thunk.getState()
  const headers: OAuth2faHeaders = {
    ...oAuth2faHeaders(state),
  }

  const { username, password } = request
  const formData = new URLSearchParams({
    username,
    password,
    'remember-me': 'false',
    'remember-me-step-up': String(rememberMeStepUpLoginParam(state)),
  })
  return await thunk.extra.portalApi().post('/portal/consumers/auth/login', formData, headers)
})

export const authLogout: PortalThunk<void, void> = portalAction('logout', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/auth/logout', {})
})

/**
 * CASHDAP-3556: why the persistent-login enforce answered 207 — the 12-month absolute cap
 * ('absolute-cap') or a risk-engine flag ('risk'), from the x-step-up-reason response header.
 * Optional end to end: older backends omit the header, and unknown values are dropped rather
 * than forwarded so the analytics dimension stays a closed set.
 */
export type PersistentStepUpReason = 'absolute-cap' | 'risk'

export interface PersistentLoginResult {
  active: boolean
  verify: boolean
  isTrustedCheckout: boolean
  allowEmail2Fa?: boolean
  stepUpReason?: PersistentStepUpReason
}

/*
Called at the beginning of checkout; will check to see if we are persistently logged in
 -> If using authorized bearer token then logged in
 -> If a persistent session exists, log in
 -> Otherwise log out
 */
export const checkPersistentLogin: PortalThunk<void, PersistentLoginResult> = portalAction('consumer/checkPersistentLogin', async (_, thunk) => {
  try {
    const state = thunk.getState()
    const transactionToken = tokenSelector(state)
    const afterpayCheckoutLoginToken = afterpayCheckoutLoginTokenSelector(state) ?? null
    const cashCheckoutToken = cashCheckoutTokenSelector(state)
    const headers: PersistentLoginHeaders = {
      ...(transactionToken !== '' && { Authorization: `Bearer ${transactionToken}` }),
      ...oAuth2faHeaders(state),
      ...(cashCheckoutToken != null && { 'x-cash-token': cashCheckoutToken }),
      ...(afterpayCheckoutLoginToken != null && { 'x-afterpay-colt': afterpayCheckoutLoginToken }),
    }
    const config = { headers, validateStatus: () => true }
    const { status, headers: responseHeaders } = await thunk.extra
      .portalApi()
      .axiosInstance.post('/portal/consumers/persistent-login/enforce', undefined, config)
    const isTrustedCheckout = responseHeaders?.['x-is-trusted-checkout'] === 'true'
    const allowEmail2Fa = responseHeaders?.['x-2fa-allow-email'] === 'true'
    const stepUpReasonHeader = responseHeaders?.['x-step-up-reason']
    const stepUpReason = stepUpReasonHeader === 'absolute-cap' || stepUpReasonHeader === 'risk' ? stepUpReasonHeader : undefined
    return {
      active: Math.floor(status / 100) === 2,
      verify: status === 207,
      allowEmail2Fa,
      isTrustedCheckout,
      stepUpReason,
    }
  } catch (e) {
    return {
      active: false,
      verify: false,
      isTrustedCheckout: false,
    }
  }
})

export const newPersistentSession: PortalThunk<void, boolean> = portalAction('consumer/newPersistentSession', async (_, thunk) => {
  try {
    await thunk.extra.portalApi().post('/portal/consumers/persistent-login/new', {})
    return true
  } catch (e) {
    return false
  }
})

/**
 * CASHDAP-3292/3258: cookie-only revoke of THIS device's remember-me session — the server cancels the
 * session behind the request's cookie (emitting the risk revoke event and the deactivation email) while
 * KEEPING the authenticated checkout session. Used by the issuance toast's "Not on this device"
 * opt-out; unlike authLogout, the consumer stays logged in and continues their checkout. Best-effort:
 * the local opt-out (localStorage + mint/resume gates) already holds device-side if this call fails.
 */
export const revokePersistentSession: PortalThunk<void, boolean> = portalAction('consumer/revokePersistentSession', async (_, thunk) => {
  try {
    await thunk.extra.portalApi().post('/portal/consumers/persistent-login/revoke', {})
    return true
  } catch (e) {
    return false
  }
})

export const sendResetLink: PortalThunk<SendResetLinkRequest, void> = portalAction('sendResetLink', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/force-password-reset/send-email', request)
})

export const get2faCode: PortalThunk<{ sendBy: VerificationMethod }, void> = portalAction('2fa/getCode', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/auth/2fa/code', request)
})

export interface RiskExtra {
  orderToken: string
  checkpoint: 'CHECKOUT_CONFIRM'
  topazTraceId: string
  paymentMethodId?: string
  paymentMethodType?: PaymentMethodType
}
export const check2faCode: PortalThunk<
  {
    code: string
    riskExtra?: RiskExtra
  },
  void
> = portalAction('2fa/checkCode', async (request, thunk) => {
  const profilingSessionId = profilingIdSelector(thunk.getState())
  return await thunk.extra.portalApi().post('/portal/consumers/auth/2fa/check-code', {
    ...request,
    profilingSessionId,
  })
})

export const check2faDob: PortalThunk<{ mobile: string; dateOfBirth: Date; lastName: string }, void> = portalAction('2fa/check-dob', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/auth/2fa/check-dob', request)
})

export const sendOneTimePassword: PortalThunk<void, void> = portalAction('otp/getCode', async (request, thunk) => {
  const { email, mobile } = consumerSelector(thunk.getState())
  const { isMobileLogin } = mobileLoginSelector(thunk.getState())

  try {
    if (isMobileLogin) {
      await thunk.extra.portalApi().post('/portal/consumers/auth/otp/mobile/send', { mobile })
    } else {
      await thunk.extra.portalApi().post('/portal/consumers/auth/otp/send', { email })
    }
  } catch (error) {
    if (error.response.status === 429) {
      error.response.data = { errorCode: ErrorCode.TooManyOTPRequests }
    }
    throw error
  }
})

export const loginWithOneTimePassword: PortalThunk<string, AuthLoginResponse> = portalAction('otp/checkCode', async (request, thunk) => {
  const transactionToken = tokenSelector(thunk.getState())
  const { email, mobile } = consumerSelector(thunk.getState())
  const { isMobileLogin } = mobileLoginSelector(thunk.getState())

  // @ts-expect-error: OPERATION BLEED STOPPER
  const formData = new URLSearchParams({
    username: isMobileLogin ? mobile : email,
    'one-time-password': request,
  })
  return await thunk.extra.portalApi().post('/portal/consumers/auth/otp/login', formData)
})

export const requestTokenFromApp: PortalThunk<void, CheckoutTokenResponse> = portalAction('consumer/requestToken', async () => {
  if (window.xprops != null && window.xprops.canHandleAfterpayTokenRequest === true && isCallable(window.xprops.postMessageToInAppBrowser)) {
    return await new Promise((resolve, reject) => {
      const errorTimer = window.setTimeout(() => {
        window.removeEventListener('message', listener)
        reject(new Error())
      }, 2000)

      const listener: (event: MessageEvent) => void = (event) => {
        try {
          const data: CheckoutTokenResponse = JSON.parse(event.data)
          if (data.type === AFTERPAY_CHECKOUT_LOGIN_TOKEN || data.type === CASH_CHECKOUT_TOKEN) {
            window.clearTimeout(errorTimer)
            window.removeEventListener('message', listener)
            resolve(data)
          }
        } catch (err) {}
      }
      window.addEventListener('message', listener)
      // @ts-expect-error: OPERATION BLEED STOPPER
      window.xprops.postMessageToInAppBrowser(JSON.stringify({ type: CHECKOUT_TOKEN_REQUEST, payload: window.location.origin }))
    })
  }
  return await Promise.reject(new Error())
})

const isJWTExpired = (jwt: string): boolean => {
  try {
    if (typeof jwt !== 'string') {
      return true
    }
    const { exp }: any = jwtDecode(jwt)
    if (exp == null) {
      return true
    }
    const currentTime = new Date().getTime() / 1000
    if (currentTime > exp) {
      return true
    }
    return false
  } catch {
    return true
  }
}

export const getCashCheckoutToken = createAsyncThunk<string | null, void, PortalThunkConfig>('consumer/get/cashToken', async (_, thunkApi) => {
  let cashCheckoutToken = cashCheckoutTokenSelector(thunkApi.getState())
  if (cashCheckoutToken != null && isJWTExpired(cashCheckoutToken)) {
    // cashCheckoutToken exists, refresh it if necessary
    // TODO: only refresh cashCheckoutToken token if the user is not linked to cashapp yet
    await thunkApi.dispatch(requestTokenFromApp())
    cashCheckoutToken = cashCheckoutTokenSelector(thunkApi.getState())
  }
  return cashCheckoutToken
})

export const getEfaCode: PortalThunk<void, { phoneNumber: string; efaCheckStatus: EFA_CHECK_STATUS }> = portalAction('efa/getCode', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/efa-sms', request)
})

export const verifyEfaCode: PortalThunk<
  {
    code: string
  },
  void
> = portalAction('efa/verifyCode', async ({ code }, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/efa-verify-code', {
    code,
  })
})
